import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useActiveOrganization } from "@/hooks/use-organizations";
import { useOrgCurrency, useUpdateOrgCurrency } from "@/hooks/use-org-settings";

const CURRENCIES = [
  { code: "UYU", label: "UYU – Peso Uruguayo ($U)" },
  { code: "ARS", label: "ARS – Peso Argentino ($)" },
  { code: "USD", label: "USD – Dólar (US$)" },
  { code: "EUR", label: "EUR – Euro (€)" },
  { code: "BRL", label: "BRL – Real (R$)" },
  { code: "CLP", label: "CLP – Peso Chileno ($)" },
  { code: "COP", label: "COP – Peso Colombiano ($)" },
  { code: "MXN", label: "MXN – Peso Mexicano ($)" },
  { code: "PEN", label: "PEN – Sol Peruano (S/)" },
  { code: "GBP", label: "GBP – Libra Esterlina (£)" },
] as const;

export function CurrencySelector() {
  const { t } = useTranslation();
  const { data: activeOrg } = useActiveOrganization();
  const currency = useOrgCurrency(activeOrg?.id);
  const updateCurrency = useUpdateOrgCurrency();

  const handleChange = (value: string) => {
    if (!activeOrg?.id) return;

    updateCurrency.mutate(
      { currency: value },
      {
        onSuccess: () => {
          toast.success(
            t("account.settings.currency.success", {
              defaultValue: "Moneda actualizada",
            }),
          );
        },
        onError: () => {
          toast.error(
            t("account.settings.currency.error", {
              defaultValue: "Error al actualizar la moneda",
            }),
          );
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t("account.settings.currency.title", {
            defaultValue: "Moneda",
          })}
        </CardTitle>
        <CardDescription>
          {t("account.settings.currency.description", {
            defaultValue: "Seleccioná la moneda en la que se muestran los precios del menú.",
          })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Select
          disabled={!activeOrg?.id || updateCurrency.isPending}
          onValueChange={handleChange}
          value={currency}
        >
          <SelectTrigger className="w-full max-w-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCIES.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardContent>
    </Card>
  );
}
